import { useEffect, useState } from 'react'
import { CmsSellerFilters } from '../../../../domain/seller'
import { HTMLInputMapId, RADIUS_TYPE_KM } from '../constants'
import { convertMilesToKm } from '../helper'

import { NaverHTMLSellersMapId } from './constants'
import { NaverFormData, InitAutoCompleteOptions } from './types'

/**
 * Load Naver Maps API and ensure it is ready
 */
export const useLoadNaverMapApi = () => {
  const [isLoaded, setIsLoaded] = useState(false)

  const ncpClientId = process.env.NEXT_PUBLIC_NAVER_MAP_CLIENT_ID

  if (!ncpClientId) {
    throw Error('Naver Maps Client ID is missing')
  }

  useEffect(() => {
    const script = document.createElement('script')
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${ncpClientId}&submodules=geocoder,panorama,overlay`
    script.async = true
    script.onload = () => setIsLoaded(true)
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [ncpClientId])

  return isLoaded
}

/**
 * Initialize Naver Maps Autocomplete for search functionality
 */
export const naverInitAutoComplete = ({
  onChangePlace,
  defautNameValue,
}: InitAutoCompleteOptions) => {
  const mapInputCitiesEl = document.querySelector<HTMLInputElement>(`#${HTMLInputMapId}`)

  if (!mapInputCitiesEl) {
    return null
  }

  if (defautNameValue) {
    mapInputCitiesEl.value = defautNameValue
  }

  // Use Naver Places or an appropriate search API for autocomplete
  const autoComplete = new naver.maps.Service.geocode({
    query: mapInputCitiesEl.value,
  }, (status, response) => {
    if (status === naver.maps.Service.Status.ERROR) return
    const place = response.result.items[0] // Get the first result
    if (place) {
      onChangePlace(place)
    }
  })

  return autoComplete
}

/**
 * Transform form data into Naver-compatible seller filters
 */
export const naverTransformFormDataToCmsSellerFilter = (
  formData: NaverFormData,
): CmsSellerFilters => {
  const filters: CmsSellerFilters = {}

  if (formData.place) {
    if (formData.place.category === 'locality') {
      filters.city = formData.place.name
    }
    if (formData.place.category === 'country') {
      filters.country = formData.place.name
    }

    if (!formData.lat && !formData.lng) {
      if (formData.place.geometry) {
        const valueRadius = formData.radius || 20

        filters.city = undefined
        filters.country = undefined
        filters.radius = {
          lat: formData.place.geometry.location.lat,
          lng: formData.place.geometry.location.lng,
          value: formData.typeRadius === RADIUS_TYPE_KM
            ? convertMilesToKm(valueRadius)
            : valueRadius,
        }
      }
    }
  }

  if (formData.type && formData.type !== 'all') {
    filters.type = formData.type
  }

  if (formData.categories?.length) {
    filters.categories = [...formData.categories]
  }

  return filters
}

/**
 * Retrieve the Naver map element
 */
export const naverGetMapEl = () =>
  document.querySelector<HTMLElement>(`#${NaverHTMLSellersMapId}`)
